import React, { useEffect, useState } from 'react';
import { Save, RefreshCw, Filter, User, ToggleLeft, ToggleRight } from 'lucide-react';
import localRequest from '../../services/LocalServer/localRequest';
import noimg from '../../Icons/checked.svg';
import Swal from 'sweetalert2';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const LockerInventory = () => {
  const history = useHistory();
  const machineID = localStorage.getItem('machineID');

  const testMode = localStorage.getItem('testMode') === 'true' ? 'true' : 'false';
  const linkToURL = `/machineUI/home/${machineID}/${testMode}`;

  const [products, setProducts] = useState([]);
  const [originalProducts, setOriginalProducts] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = () => {
    setIsLoading(true);
    localRequest({
      method: 'get',
      url: `getLinkedBoxProduct`,
    })
      .then((res) => {
        console.log('linkedBoxProduct result', res.data);
        // Transform the API data to match the component's expected format
        const transformedProducts = res.data.map((item) => ({
          uid: item.uid,
          id: item._id,
          name: item.name?.en || 'Unnamed Locker Product',
          upc: item.upc || '',
          price: item.price || 0,
          current_stock: item.current_stock || 0,
          max_stock: item.max_stock || 1, // Locker products typically have max stock of 1
          prodType: item.prodType,
          brand: item.brand,
          description: item.description?.en || '',
          productPictures: item.productPictures || [],
          isSold: item.isSold || false,
          row: item.row || 0,
          column: item.column || 0,
        }));

        setProducts(transformedProducts);
        setOriginalProducts([...transformedProducts]);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching locker products:', error);
        setIsLoading(false);
        Swal.fire({
          title: 'Error fetching products',
          text: 'Failed to load locker products',
          icon: 'error',
          showConfirmButton: false,
          timer: 1500,
        });
      });
  };

  const toggleSoldStatus = (uid) => {
    setProducts((prevProducts) =>
      prevProducts.map((product) => {
        if (product.uid === uid) {
          const newIsSold = !product.isSold;
          const updatedProduct = { 
            ...product, 
            isSold: newIsSold,
            current_stock: newIsSold ? 0 : 1 // Set stock to 0 if sold, 1 if available
          };
          return updatedProduct;
        }
        return product;
      })
    );
    setHasChanges(true);
  };

  const saveAllChanges = () => {
    setOriginalProducts([...products]);
    setHasChanges(false);

    const result = products.map((product) => {
      return {
        current_stock: product.isSold ? 0 : 1,
        isSold: product.isSold,
        uid: product.uid,
        upc: product.upc,
      };
    });

    localRequest({
      method: 'patch',
      url: 'updateLinkedBoxProduct',
      data: result,
    })
      .then((res) => {
        console.log('Update response:', res);
        Swal.fire({
          title: 'Changes saved successfully',
          icon: 'success',
          showConfirmButton: false,
          timer: 1500,
        });
      })
      .catch((error) => {
        console.error('Error updating locker products:', error);
        Swal.fire({
          title: 'Failed to save changes',
          text: 'Please try again',
          icon: 'error',
          showConfirmButton: false,
          timer: 1500,
        });
      });
  };

  const discardChanges = () => {
    setProducts([...originalProducts]);
    setHasChanges(false);
  };

  const fillAllStock = () => {
    setProducts((prevProducts) =>
      prevProducts.map((product) => ({
        ...product,
        isSold: false,
        current_stock: 1
      }))
    );
    setHasChanges(true);
  };

  const clearAllStock = () => {
    setProducts((prevProducts) =>
      prevProducts.map((product) => ({
        ...product,
        isSold: true,
        current_stock: 0
      }))
    );
    setHasChanges(true);
  };

  const backToUserMode = () => {
    history.push(linkToURL);
  };

  // Helper function to get unique columns for a specific row
  const getColumnsForRow = (rowNumber) => {
    const rowProducts = products.filter(product => product.row === rowNumber);
    const columns = [...new Set(rowProducts.map(p => p.column))].sort((a, b) => a - b);
    return columns;
  };

  // Helper function to get max columns across all rows
  const getMaxColumns = () => {
    const allColumns = products.map(p => p.column).filter(col => col && col > 0);
    return allColumns.length > 0 ? Math.max(...allColumns) : 4;
  };

  if (isLoading) {
    return (
      <div
        className="h-screen w-full bg-gray-100 flex items-center justify-center"
        style={{ width: '1080px', height: '1920px' }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading locker products...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen w-full bg-gray-100 overflow-hidden flex flex-col"
      style={{ width: '1080px', height: '1920px' }}
    >
      {/* Header */}
      <div className="bg-white shadow-sm border-b p-4 flex-shrink-0">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Locker Inventory</h1>
            <p className="text-gray-600">
              {products.length} Locker Products
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={backToUserMode}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded font-medium"
            >
              <User size={16} className="inline mr-1" />
              User Mode
            </button>
            {hasChanges && (
              <button
                onClick={discardChanges}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded font-medium"
              >
                <RefreshCw size={16} className="inline mr-1" />
                Discard
              </button>
            )}
            <button
              onClick={saveAllChanges}
              disabled={!hasChanges}
              className={`px-4 py-2 rounded font-medium ${
                hasChanges
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Save size={16} className="inline mr-1" />
              Save All
            </button>
          </div>
        </div>

        {/* Filter Controls and Bulk Actions */}
        <div className="flex gap-4 items-center justify-between">
          <div className="flex gap-2">
            <div className="px-3 py-2 bg-orange-600 text-white rounded font-medium text-sm">
              <Filter size={14} className="inline mr-1" />
              Locker Products
            </div>
          </div>
          
          {/* Bulk Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={fillAllStock}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium text-sm"
            >
              Fill All Stock
            </button>
            <button
              onClick={clearAllStock}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium text-sm"
            >
              Clear All Stock
            </button>
          </div>
        </div>
      </div>

      {/* Products Grid by Row and Column */}
      <div className="flex-1 overflow-y-auto p-4">
        {products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No locker products found</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Row 1 */}
            {products.some(p => p.row === 1) && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">Row 1</h3>
                <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${getMaxColumns()}, 1fr)` }}>
                  {getColumnsForRow(1).map(columnNum => {
                    const product = products.find(p => p.row === 1 && p.column === columnNum);
                    return (
                      <div key={product.uid} className="bg-white rounded-lg shadow-sm border p-3">
                        <div className="text-center mb-2">
                          <div className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            Column {product.column}
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-center gap-2 mb-3">
                          <div className="flex-shrink-0">
                            <img
                              src={
                                product.productPictures && product.productPictures.length > 0
                                  ? `${process.env.REACT_APP_LOCAL_API}/pictures/linkedBoxProduct/${product.productPictures[0]}`
                                  : noimg
                              }
                              alt={product.name}
                              className="w-12 h-15 object-cover rounded-md bg-gray-100"
                              onError={(e) => {
                                e.target.src = noimg;
                              }}
                            />
                          </div>
                          <div className="text-center min-w-0">
                            <h4 className="font-semibold text-gray-800 text-xs mb-1 truncate">
                              {product.name}
                            </h4>
                            <p className="text-xs text-gray-500 font-mono">{product.upc}</p>
                            <p className="text-sm font-bold text-gray-800">${product.price.toFixed(2)}</p>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center justify-center gap-2">
                            <span className="text-xs text-gray-600">Status:</span>
                            <span 
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                product.isSold || product.current_stock === 0
                                  ? 'bg-red-100 text-red-700' 
                                  : 'bg-green-100 text-green-700'
                              }`}
                            >
                              {product.isSold || product.current_stock === 0 ? 'Sold' : 'Available'}
                            </span>
                          </div>

                          <div className="flex justify-center">
                            <button
                              onClick={() => toggleSoldStatus(product.uid)}
                              className={`flex items-center gap-1 px-3 py-1 rounded font-medium text-xs transition-colors ${
                                product.isSold || product.current_stock === 0
                                  ? 'bg-green-500 hover:bg-green-600 text-white'
                                  : 'bg-gray-800 hover:bg-gray-900 text-white'
                              }`}
                            >
                              {product.isSold || product.current_stock === 0 ? (
                                <>
                                  <ToggleRight size={12} />
                                  Available
                                </>
                              ) : (
                                <>
                                  <ToggleLeft size={12} />
                                  Sold
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Row 2 */}
            {products.some(p => p.row === 2) && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">Row 2</h3>
                <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${getMaxColumns()}, 1fr)` }}>
                  {getColumnsForRow(2).map(columnNum => {
                    const product = products.find(p => p.row === 2 && p.column === columnNum);
                    return (
                      <div key={product.uid} className="bg-white rounded-lg shadow-sm border p-3">
                        <div className="text-center mb-2">
                          <div className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            Column {product.column}
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-center gap-2 mb-3">
                          <div className="flex-shrink-0">
                            <img
                              src={
                                product.productPictures && product.productPictures.length > 0
                                  ? `${process.env.REACT_APP_LOCAL_API}/pictures/linkedBoxProduct/${product.productPictures[0]}`
                                  : noimg
                              }
                              alt={product.name}
                              className="w-12 h-15 object-cover rounded-md bg-gray-100"
                              onError={(e) => {
                                e.target.src = noimg;
                              }}
                            />
                          </div>
                          <div className="text-center min-w-0">
                            <h4 className="font-semibold text-gray-800 text-xs mb-1 truncate">
                              {product.name}
                            </h4>
                            <p className="text-xs text-gray-500 font-mono">{product.upc}</p>
                            <p className="text-sm font-bold text-gray-800">${product.price.toFixed(2)}</p>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center justify-center gap-2">
                            <span className="text-xs text-gray-600">Status:</span>
                            <span 
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                product.isSold || product.current_stock === 0
                                  ? 'bg-red-100 text-red-700' 
                                  : 'bg-green-100 text-green-700'
                              }`}
                            >
                              {product.isSold || product.current_stock === 0 ? 'Sold' : 'Available'}
                            </span>
                          </div>

                          <div className="flex justify-center">
                            <button
                              onClick={() => toggleSoldStatus(product.uid)}
                              className={`flex items-center gap-1 px-3 py-1 rounded font-medium text-xs transition-colors ${
                                product.isSold || product.current_stock === 0
                                  ? 'bg-green-500 hover:bg-green-600 text-white'
                                  : 'bg-gray-800 hover:bg-gray-900 text-white'
                              }`}
                            >
                              {product.isSold || product.current_stock === 0 ? (
                                <>
                                  <ToggleRight size={12} />
                                  Available
                                </>
                              ) : (
                                <>
                                  <ToggleLeft size={12} />
                                  Sold
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LockerInventory;